import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Check, X, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const empty = { name: "", email: "", password: "", role: "staff" };

export default function Users() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  const sigRef = useRef(null);
  const [sigTarget, setSigTarget] = useState(null);

  const load = () => api.get("/auth/users").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error("Name, email aur password zaroori hain");
      return;
    }
    if (!signature) {
      toast.error("Signature image upload karna zaroori hai");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("password", form.password);
      fd.append("role", form.role);
      fd.append("signature", signature);
      await api.post("/auth/users", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("User created");
      setOpen(false); setForm(empty); setSignature(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete user?")) return;
    try { await api.delete(`/auth/users/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const triggerSig = (uid) => { setSigTarget(uid); setTimeout(() => sigRef.current?.click(), 0); };
  const onSigFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !sigTarget) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/auth/users/${sigTarget}/signature`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Signature updated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { if (sigRef.current) sigRef.current.value = ""; setSigTarget(null); }
  };

  const Badge = ({ ok }) => ok
    ? <span className="inline-flex items-center gap-1 text-green-700 text-xs"><Check size={13}/> Uploaded</span>
    : <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><X size={13}/> Missing</span>;

  return (
    <div className="p-8" data-testid="users-page">
      <div className="flex items-start justify-between mb-6">
        <div><h1 className="font-display text-3xl text-slate-900">Users</h1><p className="text-slate-500 text-sm">Manage admin & staff accounts with signature</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-primary" onClick={()=>{setForm(empty);setSignature(null);}} data-testid="new-user-btn"><Plus size={16}/> New User</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create User</DialogTitle>
              <DialogDescription>Add an admin or staff account. A signature image is required.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="user-name-input"/></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="user-email-input"/></div>
              <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} data-testid="user-password-input"/></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Signature (image) <span className="text-red-500">*</span></Label>
                <Input type="file" accept="image/*" onChange={(e)=>setSignature(e.target.files?.[0] || null)} data-testid="user-signature-input"/>
                {signature && <div className="text-xs text-green-700 mt-1">{signature.name}</div>}
              </div>
            </div>
            <DialogFooter><Button onClick={save} disabled={saving} className="btn-primary" data-testid="save-user-btn">{saving ? "Creating…" : "Create"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <input ref={sigRef} type="file" accept="image/*" onChange={onSigFile} className="hidden" data-testid="user-asset-file-input"/>

      <div className="bg-white border border-slate-100 rounded-xl">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600"><tr>
            <th className="text-left px-4 py-2">Name</th>
            <th className="text-left px-4 py-2">Email</th>
            <th className="text-left px-4 py-2">Role</th>
            <th className="text-left px-4 py-2">Signature</th>
            <th className="text-right px-4 py-2">Actions</th>
          </tr></thead>
          <tbody>
            {items.map(u=>(
              <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-2 font-medium">{u.name}</td>
                <td className="px-4 py-2">{u.email}</td>
                <td className="px-4 py-2 capitalize">{u.role}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Badge ok={u.has_signature}/>
                    <button onClick={()=>triggerSig(u.id)} className="text-xs text-[#0b5cc4] hover:underline inline-flex items-center gap-1" data-testid={`upload-signature-${u.email}`}><Upload size={12}/>{u.has_signature ? "Replace" : "Upload"}</button>
                  </div>
                </td>
                <td className="px-4 py-2 text-right">
                  {u.id !== user?.id && <button onClick={()=>remove(u.id)} className="p-2 hover:bg-red-50 rounded text-red-600" data-testid={`delete-user-${u.email}`}><Trash2 size={16}/></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
